import type { ParsedCountry } from "../types/PhoneInput";

export const validateEmail = (email: string): string | null => {
  if (!email.trim()) {
    return "Email is required";
  }

  // RFC 5322 simplified regex (good enough for most cases)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return "Please enter a valid email address";
  }

  return null;
};

export const validateText = (text: string, name?: string): string | null => {
  if (!text.trim()) {
    return `${name} is required`;
  }
  if (text.length <= 1) {
    return `${name} must be longer than 2 characters`;
  }
  return null;
};

/**
 * Validate password strength
 * Used in: Login, Register, Password Change
 *
 * Requirements (from client):
 * - Minimum 8 characters
 * - Must contain uppercase (A-Z)
 * - Must contain lowercase (a-z)
 * - Must contain number (0-9)
 * - Must contain special character (!@#$%^&* etc.)
 **/
export const validatePassword = (password: string): string | null => {
  if (!password) {
    return "Password is required";
  }

  // Rule 1: Minimum length
  if (password.length < 8) {
    return "Password must be at least 8 characters";
  }

  // Rule 2: Uppercase letter
  if (!/[A-Z]/.test(password)) {
    return "Password must contain an uppercase letter";
  }

  // Rule 3: Lowercase letter
  if (!/[a-z]/.test(password)) {
    return "Password must contain a lowercase letter";
  }

  // Rule 4: Number
  if (!/[0-9]/.test(password)) {
    return "Password must contain a number";
  }

  // Rule 5: Special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return "Password must contain a special character (!@#$%^&* etc.)";
  }

  return null;
};

export const validatePasswordConfirm = (
  password: string,
  confirmPassword: string
): string | null => {
  if (!confirmPassword) {
    return "Please confirm your password";
  }

  if (password !== confirmPassword) {
    return "Passwords do not match";
  }

  return null;
};

export function removePhoneFormatting(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, "");
}

export function getPhoneDigitsOnly(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, "");
}

function countDigitsInPattern(formatPattern: string): number {
  return (formatPattern.match(/\./g) || []).length;
}

function getFormatPatternForNumber(
  phoneNumber: string,
  format: string | Record<string, string>
): string | null {
  // If format is just a string, use it directly
  if (typeof format === "string") {
    return format;
  }

  // If format is an object with regex patterns
  if (typeof format === "object") {
    // Check each regex pattern
    for (const [pattern, formatString] of Object.entries(format)) {
      if (pattern === "default") {
        continue; // Skip default for now, use it as fallback
      }

      // Convert string pattern to regex
      // Pattern looks like "/^50/" or "/^(2|3|4|6|7|9)/"
      const regexMatch =
        pattern.match(/^\/\^(.+)\/$/) || pattern.match(/^\/(.+)\/$/);

      if (regexMatch) {
        const regexStr = regexMatch[1];
        const regex = new RegExp(`^${regexStr}`);

        if (regex.test(phoneNumber)) {
          return formatString;
        }
      }
    }

    // Use default if no pattern matched
    return format.default || null;
  }

  return null;
}

function applyFormat(phoneNumber: string, formatPattern: string): string {
  let formatted = "";
  let digitIndex = 0;

  for (let i = 0; i < formatPattern.length; i++) {
    if (formatPattern[i] === ".") {
      if (digitIndex < phoneNumber.length) {
        formatted += phoneNumber[digitIndex];
        digitIndex++;
      }
    } else {
      formatted += formatPattern[i];
    }
  }

  return formatted;
}

export function formatPhoneNumber(
  phoneNumber: string,
  country: ParsedCountry | null
): string | null {
  if (!phoneNumber || !country) {
    return phoneNumber || null;
  }

  const digitsOnly = removePhoneFormatting(phoneNumber);

  if (!digitsOnly) {
    return null;
  }

  // If country has no format, reject
  if (!country.format) {
    return null;
  }

  // Get the format pattern for this number
  const formatPattern = getFormatPatternForNumber(digitsOnly, country.format);

  if (!formatPattern) {
    return null;
  }

  // Count expected digits in pattern
  const expectedDigits = countDigitsInPattern(formatPattern);

  // STRICT: Only format if digit count matches exactly
  if (digitsOnly.length !== expectedDigits) {
    return null; // Invalid length for this pattern
  }

  return applyFormat(digitsOnly, formatPattern);
}

export function formatInternationalPhone(
  phoneNumber: string,
  country: ParsedCountry | null
): string | null {
  if (!phoneNumber || !country) {
    return null;
  }

  const formatted = formatPhoneNumber(phoneNumber, country);

  if (!formatted) {
    return null;
  }

  return `${country.dialCode} ${formatted}`;
}

export function isValidPhoneFormat(
  phoneNumber: string,
  country: ParsedCountry | null
): boolean {
  if (!phoneNumber || !country || !country.format) {
    return false;
  }

  const digitsOnly = removePhoneFormatting(phoneNumber);
  const formatPattern = getFormatPatternForNumber(digitsOnly, country.format);

  if (!formatPattern) {
    return false;
  }

  // Must have exactly the right number of digits
  const expectedDigits = countDigitsInPattern(formatPattern);
  return digitsOnly.length === expectedDigits;
}

export function getValidPhoneLengths(country: ParsedCountry | null): number[] {
  if (!country || !country.format) {
    return [];
  }

  const lengths = new Set<number>();

  if (typeof country.format === "string") {
    lengths.add(countDigitsInPattern(country.format));
  } else if (typeof country.format === "object") {
    for (const formatString of Object.values(country.format)) {
      lengths.add(countDigitsInPattern(formatString));
    }
  }

  return Array.from(lengths).sort((a, b) => a - b);
}

export function getPhoneDisplayText(
  phoneNumber: string,
  country: ParsedCountry | null
): {
  raw: string;
  formatted: string | null;
  international: string | null;
  isValid: boolean;
  expectedLengths: number[];
} {
  if (!phoneNumber || !country) {
    return {
      raw: phoneNumber,
      formatted: null,
      international: null,
      isValid: false,
      expectedLengths: getValidPhoneLengths(country),
    };
  }

  const raw = removePhoneFormatting(phoneNumber);
  const formatted = formatPhoneNumber(phoneNumber, country);
  const international = formatInternationalPhone(phoneNumber, country);
  const isValid = formatted !== null;
  const expectedLengths = getValidPhoneLengths(country);

  return {
    raw,
    formatted,
    international,
    isValid,
    expectedLengths,
  };
}

export const validatePhoneNumber = (phoneNumber: string): string | null => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return "Phone number is required";
  }

  // Remove all non-digits
  const digitsOnly = removePhoneFormatting(phoneNumber);

  if (!digitsOnly) {
    return "Phone number must contain at least one digit";
  }

  // Check reasonable phone length (7-15 digits)
  if (digitsOnly.length < 7) {
    return "Phone number is too short (minimum 7 digits)";
  }

  if (digitsOnly.length > 15) {
    return "Phone number is too long (maximum 15 digits)";
  }

  return null;
};

export const validatePhoneWithCountry = (
  phoneNumber: string,
  country: ParsedCountry | null
): string | null => {
  if (!phoneNumber) {
    return "Phone number is required";
  }

  if (!country) {
    return "Please select a country";
  }

  // Use the strict validation from usePhoneInput hook approach
  const display = getPhoneDisplayText(phoneNumber, country);

  if (!display.isValid) {
    return `Invalid phone for ${country.name}.`;
  }

  return null;
};

export const getPasswordStrength = (
  password: string
): {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  isStrong: boolean;
} => {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    isStrong:
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
};

export const validateLoginForm = (data: {
  email: string;
  password: string;
}): Record<string, string | null> => {
  return {
    email: validateEmail(data.email),
    password: validatePassword(data.password),
  };
};

export const validateRegisterForm = (data: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  country?: ParsedCountry | null;
}): Record<string, string | null> => {
  // If country is provided, use strict validation
  const phoneError = data.country
    ? validatePhoneWithCountry(data.phone, data.country)
    : validatePhoneNumber(data.phone);

  return {
    firstName: validateText(data.firstName, "First name"),
    lastName: validateText(data.lastName, "Last name"),
    email: validateEmail(data.email),
    password: validatePassword(data.password),
    confirmPassword: validatePasswordConfirm(
      data.password,
      data.confirmPassword
    ),
    phone: phoneError,
  };
};

export const validatePasswordChangeForm = (data: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Record<string, string | null> => {
  return {
    currentPassword: validatePassword(data.currentPassword),
    newPassword: validatePassword(data.newPassword),
    confirmPassword: validatePasswordConfirm(
      data.newPassword,
      data.confirmPassword
    ),
  };
};

export const hasValidationErrors = (
  errors: Record<string, string | null>
): boolean => {
  return Object.values(errors).some((error) => error !== null);
};

export const getFirstError = (
  errors: Record<string, string | null>
): { field: string; message: string } | null => {
  const firstErrorEntry = Object.entries(errors).find(
    ([_, error]) => error !== null
  );
  return firstErrorEntry
    ? { field: firstErrorEntry[0], message: firstErrorEntry[1] as string }
    : null;
};
